import { fetchAirMeshToken, fetchAirMeshChannels } from 'apis/airspace.api';
import {
	setAMConnectionLoading,
	setAMConnectionError,
	setAMWebSocketConnected,
	resetAMState,
	airMeshChannelsSelector,
	setReconnectAttempts,
	setNextRetryTime
} from 'reducers/liveTrafficSlice';
import { processData } from 'utils/airMeshHelpers';
import { fetchEventSource } from '@microsoft/fetch-event-source';
let abortController = null;
let tokenRefreshTimeout = null;
let pollingInterval = null;
let autoReconnectTimeout = null;
let isIntentionalAbort = false;
let reconnectAttempts = 0;
const RETRY_INTERVAL = 60000; // 1 minute
const AUTO_RECONNECT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const AUTO_RECONNECT_WAIT = 1 * 1000; // 1 second
const BATCH_FLUSH_INTERVAL = 500; // 500ms
// const DECODE_LOG_WINDOW_MS = 30 * 1000; // 30 seconds

// Topics list - constant to avoid recreating on each connection
// const SSE_TOPICS = [
// 	'private-adsb-exchange',
// 	'private-uavionix',
// 	'private-casia-g',
// 	'private-airdex',
// 	'private-uas-sentry',
// 	'private-uspace-nid',
// 	'private-aero',
// 	'private-rigitech',
// 	'private-internal-simulator',
// 	'private-dronetag-public',
// 	'private-hidden-level'
// ];

let hasScheduledRetry = false;
let isHandlingError = false; // NEW: guard for error handling
let isRetrying = false;
let isFirstChannelPoll = true; // NEW: flag to skip first channel difference check
let isAutoReconnecting = false; // Flag to indicate auto-reconnect is in progress

const getJWTExpiration = token => {
	const payload = JSON.parse(atob(token.split('.')[1]));
	return payload.exp * 1000;
};

const scheduleTokenRefresh = expiresAt => (dispatch, getState) => {
	const timeUntilExpiry = expiresAt - Date.now();
	const refreshBuffer = 2 * 60 * 1000; // 2 minutes
	const refreshTime = Math.max(0, timeUntilExpiry - refreshBuffer);
	if (tokenRefreshTimeout) {
		clearTimeout(tokenRefreshTimeout);
	}

	const refresh = async (isRetry = false) => {
		try {
			await dispatch(subscribeToSSE(false));
		} catch (error) {
			console.error('[SSE] Token refresh failed:', error);
			if (!isRetry) {
				setTimeout(() => refresh(true), 5000);
			}
		}
	};

	if (refreshTime <= 0) {
		refresh();
		return;
	}
	tokenRefreshTimeout = setTimeout(refresh, refreshTime);
};

export const pollAirmeshChannels = () => async (dispatch, getState) => {
	if (pollingInterval) {
		clearInterval(pollingInterval);
	}
	const pollChannels = async () => {
		const previousChannels = airMeshChannelsSelector(getState());
		await dispatch(fetchAirMeshChannels());
		const latestChannels = airMeshChannelsSelector(getState());

		// Skip the first channel difference check
		if (isFirstChannelPoll) {
			isFirstChannelPoll = false;
			return;
		}

		// Optimize: Use shallow comparison first, then deep if needed
		if (
			latestChannels.length !== previousChannels.length ||
			JSON.stringify(latestChannels) !== JSON.stringify(previousChannels)
		) {
			await dispatch(subscribeToSSE(false));
		}
	};
	pollingInterval = setInterval(pollChannels, 60000);
	await pollChannels();
};

let sseBuffer = [];
let flushTimeout = null;

// let decodeLogWindowStart = null;

// let decodeLogRawBytes = 0;
// let decodeLogDecodedTextBytes = 0;

// let rtTotalMs = 0;
// let rtTimes = [];
// let rtCount = 0;

// const percentile = (arr, p) => {
// 	if (!arr.length) return 0;
// 	const sorted = arr.slice().sort((a, b) => a - b);
// 	return sorted[Math.floor(sorted.length * p)] ?? 0;
// };

// const maybeLogDecodeStats = () => {
// 	const now = performance.now();
// 	if (decodeLogWindowStart == null) {
// 		decodeLogWindowStart = now;
// 		return;
// 	}

// 	const elapsedMs = now - decodeLogWindowStart;
// 	if (elapsedMs < DECODE_LOG_WINDOW_MS) return;

// 	const elapsedSec = elapsedMs / 1000;

// 	const rawKBps = decodeLogRawBytes / elapsedSec / 1024;
// 	const decodedKBps = decodeLogDecodedTextBytes / elapsedSec / 1024;

// 	const rtAvg = rtCount ? rtTotalMs / rtCount : 0;
// 	const rtP95 = percentile(rtTimes, 0.95);
// 	const rtP99 = percentile(rtTimes, 0.99);

// 	console.log(
// 		`[SSE decode] avg over 30s: ${rtAvg.toFixed(2)} ms p95 ${rtP95.toFixed(2)} ms p99 ${rtP99.toFixed(2)} ms | raw ${rawKBps.toFixed(2)} KB/s | decodedText ${decodedKBps.toFixed(2)} KB/s | ${rtCount} messages`
// 	);

// 	// reset window stats
// 	decodeLogWindowStart = now;
// 	decodeLogRawBytes = 0;
// 	decodeLogDecodedTextBytes = 0;

// 	rtTotalMs = 0;
// 	rtTimes = [];
// 	rtCount = 0;
// };

const clearSseBuffer = () => {
	sseBuffer = [];
	if (flushTimeout) {
		clearTimeout(flushTimeout);
		flushTimeout = null;
	}
};

// Helper to remove integer suffix from provider string (e.g., 'airmesh_sdk_12' -> 'airmesh_sdk')
const removeIntFromProvider = string => {
	if (!string) return '';
	return string.replace(/_\d+/g, '');
};

// Check if item should get real-time updates (all airmesh_sdk traffic)
function hasRealTimeData(item) {
	if (!item?.provider) return false;
	const provider = removeIntFromProvider(item.provider);
	return provider === 'airmesh_sdk' || provider === 'aloft_simulator';
}

function bufferAndDispatch(dispatch, incomingItems) {
	if (!incomingItems?.length) return;

	// Use filter for better performance with large arrays
	const realTimeItems = incomingItems.filter(hasRealTimeData);
	const batchedItems = incomingItems.filter(item => !hasRealTimeData(item));

	if (realTimeItems.length) {
		dispatch(processData(realTimeItems));
	}

	if (batchedItems.length) {
		sseBuffer.push(...batchedItems);
		if (!flushTimeout) {
			flushTimeout = setTimeout(() => {
				const batch = sseBuffer;
				sseBuffer = [];
				flushTimeout = null;
				dispatch(processData(batch));
			}, BATCH_FLUSH_INTERVAL);
		}
	}
}

// Internal retry function
const retryConnection = (dispatch, isManualRetry = false) => {
	if (hasScheduledRetry && !isManualRetry) {
		return;
	}
	hasScheduledRetry = true;
	isRetrying = true;

	if (window.retryTimeout) {
		clearTimeout(window.retryTimeout);
		window.retryTimeout = null;
	}

	reconnectAttempts += 1;
	dispatch(setReconnectAttempts(reconnectAttempts));

	const delay = reconnectAttempts === 1 ? 0 : RETRY_INTERVAL;

	// Set the next retry time for the progress indicator
	const nextRetryTime = Date.now() + delay;
	dispatch(setNextRetryTime(nextRetryTime));

	// Only set error state if we're past the first retry
	if (reconnectAttempts > 1) {
		dispatch(setAMConnectionError(true));
	}

	window.retryTimeout = setTimeout(() => {
		window.retryTimeout = null;
		hasScheduledRetry = false;
		isHandlingError = false;
		isRetrying = false;
		dispatch(subscribeToSSE(false));
	}, delay);
};

// Export manual retry action creator
export const manualRetry = () => async dispatch => {
	if (hasScheduledRetry) {
		if (window.retryTimeout) {
			clearTimeout(window.retryTimeout);
			window.retryTimeout = null;
		}
		hasScheduledRetry = false;
	}

	hasScheduledRetry = true;
	isRetrying = true;

	reconnectAttempts += 1;
	dispatch(setReconnectAttempts(reconnectAttempts));

	// Set the next retry time for the progress indicator
	const nextRetryTime = Date.now();
	dispatch(setNextRetryTime(nextRetryTime));

	// Only set error state if we're past the first retry
	if (reconnectAttempts > 1) {
		dispatch(setAMConnectionError(true));
	}

	// Execute retry immediately
	hasScheduledRetry = false;
	isHandlingError = false;
	isRetrying = false;
	await dispatch(subscribeToSSE(false));
};

const gunzip = async bytes => {
	const ds = new DecompressionStream('gzip');
	const stream = new Blob([bytes]).stream().pipeThrough(ds);
	return await new Response(stream).text();
};

const decodeData = async raw => {
	// 1) Try plain JSON
	try {
		const parsed = JSON.parse(raw);
		const decodedTextBytes = new TextEncoder().encode(raw).length;
		return { data: parsed.data, decodedTextBytes, wasGzip: false };
	} catch {
		//Not plain JSON
	}

	// 2) base64 -> bytes
	let binaryString;
	try {
		binaryString = atob(raw.trim());
	} catch {
		binaryString = atob(raw.replace(/\s/g, ''));
	}
	const bytes = (() => {
		const len = binaryString.length;
		const out = new Uint8Array(len);
		for (let i = 0; i < len; i++) out[i] = binaryString.charCodeAt(i);
		return out;
	})();

	// 3) gzip?
	if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
		const text = await gunzip(bytes);
		const parsed = JSON.parse(text);
		const decodedTextBytes = new TextEncoder().encode(text).length;
		return { data: parsed.data, decodedTextBytes, wasGzip: true };
	}

	// 4) plain base64 JSON
	const text = new TextDecoder().decode(bytes);
	const parsed = JSON.parse(text);
	const decodedTextBytes = new TextEncoder().encode(text).length;
	return { data: parsed.data, decodedTextBytes, wasGzip: false };
};

export const subscribeToSSE =
	(showLoader = true) =>
	async (dispatch, getState) => {
		// So worker batch handler can dispatch when USE_SSE_DECODE_WORKER is true
		window.__sseDispatchRef = dispatch;

		const isAutoReconnect = isAutoReconnecting;
		isIntentionalAbort = false;
		isHandlingError = false;

		// Only clear buffer if this is not an auto-reconnect (preserve state during auto-reconnect)
		if (!isAutoReconnect) {
			clearSseBuffer();
		}

		// Clear any existing auto-reconnect timeout before starting new connection
		if (autoReconnectTimeout) {
			clearTimeout(autoReconnectTimeout);
			autoReconnectTimeout = null;
		}

		// Only show loader and reset attempts on first connection
		if (showLoader && reconnectAttempts === 0) {
			dispatch(setAMConnectionLoading(true));
		}

		// Only reset state on first connection
		if (showLoader && reconnectAttempts === 0) {
			if (window.retryTimeout) {
				clearTimeout(window.retryTimeout);
				window.retryTimeout = null;
			}
			hasScheduledRetry = false;
			reconnectAttempts = 0;
			dispatch(setReconnectAttempts(0));
			dispatch(setNextRetryTime(null));
		}

		if (abortController) {
			abortController.abort();
		}

		abortController = new AbortController();

		try {
			const { token, url } = await dispatch(fetchAirMeshToken());
			const expiresAt = getJWTExpiration(token);
			dispatch(scheduleTokenRefresh(expiresAt));

			const urlToUse = new URL(url);
			urlToUse.searchParams.append('topic', '*');

			// for (const topic of SSE_TOPICS) {
			// 	urlToUse.searchParams.append('topic', topic);
			// }

			try {
				await fetchEventSource(urlToUse.toString(), {
					method: 'GET',
					withCredentials: true,
					headers: {
						Authorization: `Bearer ${token}`,
						Accept: 'text/event-stream'
					},
					signal: abortController.signal,
					openWhenHidden: true,
					retry: false,

					onopen: async response => {
						if (!response.ok) {
							throw new Error(`Server returned ${response.status}`);
						}

						reconnectAttempts = 0;
						hasScheduledRetry = false;
						isHandlingError = false;
						dispatch(setAMWebSocketConnected(true));
						dispatch(setAMConnectionError(false));
						dispatch(setAMConnectionLoading(false));
						dispatch(setNextRetryTime(null));
						if (window.retryTimeout) {
							clearTimeout(window.retryTimeout);
							window.retryTimeout = null;
						}
						if (!pollingInterval) {
							dispatch(pollAirmeshChannels());
						}

						// Schedule automatic reconnect after 5 minutes
						if (autoReconnectTimeout) {
							clearTimeout(autoReconnectTimeout);
						}
						autoReconnectTimeout = setTimeout(async () => {
							// Set flag to indicate this is an auto-reconnect (preserves state)
							isAutoReconnecting = true;
							// Disconnect current connection
							// Set flag to indicate this is an intentional disconnect for auto-reconnect
							isIntentionalAbort = true;
							if (abortController) {
								abortController.abort();
								abortController = null;
							}

							dispatch(setAMConnectionLoading(false));

							await new Promise(resolve => setTimeout(resolve, AUTO_RECONNECT_WAIT));
							// Reconnect without showing loader (keep table visible with existing data)
							await dispatch(subscribeToSSE(false));
							// Reset auto-reconnect flag after successful reconnection
							isAutoReconnecting = false;
						}, AUTO_RECONNECT_INTERVAL);
					},

					onmessage: async event => {
						try {
							const rawPayload = event.data;

							// const rawBytes =
							// 	typeof rawPayload === 'string' ? new TextEncoder().encode(rawPayload).length : 0;

							// decodeLogRawBytes += rawBytes;

							// Fallback: decode on main thread (your existing path)
							// const decodeStart = performance.now();
							const {
								data: processedData
								//  decodedTextBytes
							} = await decodeData(rawPayload);
							// const decodeMs = performance.now() - decodeStart;

							// rtTotalMs += decodeMs;
							// rtTimes.push(decodeMs);
							// rtCount += 1;
							// decodeLogDecodedTextBytes += decodedTextBytes;

							if (processedData?.length) bufferAndDispatch(dispatch, processedData);

							// maybeLogDecodeStats();
						} catch (error) {
							console.error('[SSE] Failed to process message:', error);
						}
					},

					onclose: () => {
						// Clear auto-reconnect timeout if connection closes (unless it's an auto-reconnect)
						if (autoReconnectTimeout && !isAutoReconnecting) {
							clearTimeout(autoReconnectTimeout);
							autoReconnectTimeout = null;
						}
						if (isIntentionalAbort || abortController?.signal.aborted) {
							//[SSE] Connection closed intentionally
							// Don't clear state if this is an auto-reconnect
							return;
						} else {
							// Server closed the connection - we should retry
							dispatch(setAMWebSocketConnected(false));
							dispatch(setAMConnectionLoading(false));
							retryConnection(dispatch, false);
						}
					},

					onerror: err => {
						console.error('[SSE] onerror', err);
						// Clear auto-reconnect timeout on error
						if (autoReconnectTimeout) {
							clearTimeout(autoReconnectTimeout);
							autoReconnectTimeout = null;
						}
						// Reset auto-reconnect flag on error
						if (isAutoReconnecting) {
							isAutoReconnecting = false;
						}
						if (abortController) {
							abortController.abort(); // Stop any browser retries
						}
						if (!isIntentionalAbort && !isHandlingError) {
							isHandlingError = true;
							dispatch(setAMWebSocketConnected(false));
							dispatch(setAMConnectionLoading(false));
							retryConnection(dispatch, false);
						}
						// 👇 This is key! Stop the fetchEventSource loop.
						throw new Error('[SSE] onerror fatal — terminating fetchEventSource');
					}
				});
			} catch (error) {
				if (abortController) {
					abortController.abort();
				}
				// Handle initial connection failure
				if (!isIntentionalAbort && !isHandlingError) {
					isHandlingError = true;
					// Reset auto-reconnect flag on error
					if (isAutoReconnecting) {
						isAutoReconnecting = false;
					}
					dispatch(setAMWebSocketConnected(false));
					dispatch(setAMConnectionError(true));
					dispatch(setAMConnectionLoading(false));
					retryConnection(dispatch, false);
				}
			}
		} catch (error) {
			// Handle token fetch failure
			if (!isIntentionalAbort && !isHandlingError) {
				isHandlingError = true;
				// Reset auto-reconnect flag on error
				if (isAutoReconnecting) {
					isAutoReconnecting = false;
				}
				dispatch(setAMWebSocketConnected(false));
				dispatch(setAMConnectionError(true));
				dispatch(setAMConnectionLoading(false));
				retryConnection(dispatch, false);
			}
		}
	};

export const stopAirmeshChannelPolling = () => {
	if (pollingInterval) {
		clearInterval(pollingInterval);
		pollingInterval = null;
	}
};

export const unsubscribeFromSSE = () => async (dispatch, getState) => {
	// If we're in the middle of a retry, let it complete
	if (isRetrying) {
		return;
	}
	window.__sseDispatchRef = null;

	// Prevent any buffered dispatch from re-populating traffic after disconnect
	clearSseBuffer();
	isIntentionalAbort = true;
	isAutoReconnecting = false; // Reset auto-reconnect flag on manual unsubscribe
	reconnectAttempts = 0;
	hasScheduledRetry = false;
	isHandlingError = false;
	isFirstChannelPoll = true; // Reset the flag when unsubscribing
	dispatch(setReconnectAttempts(0));
	dispatch(setNextRetryTime(null));
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
	stopAirmeshChannelPolling();
	if (tokenRefreshTimeout) {
		clearTimeout(tokenRefreshTimeout);
		tokenRefreshTimeout = null;
	}
	if (autoReconnectTimeout) {
		clearTimeout(autoReconnectTimeout);
		autoReconnectTimeout = null;
	}
	if (window.retryTimeout) {
		clearTimeout(window.retryTimeout);
		window.retryTimeout = null;
	}
	dispatch(setAMWebSocketConnected(false));
	dispatch(setAMConnectionError(false));
	dispatch(setAMConnectionLoading(false));
	dispatch(resetAMState());
};
